import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate, useParams } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-company-policy/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'
import AppModalFilePreview from 'src/components/modal/AppModalFilePreview'
import AppCardHeader from 'src/components/card/AppCardHeader'
import FormFileThumbnail from '../../components/form/general-input-form/FormFileThumbnail'

// helpers
import Axios from '../../helpers/axios'
import { 
  singlePolicyTypeOptionsSelect,
} from '../../helpers/format-options'
import useToastMessage from 'src/helpers/toast-message'

const EditCompanyPolicy = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const params = useParams()
  const { id } = params
  const { showSuccess, showError } = useToastMessage();

  const isLoading = useSelector((state) => state.isLoading)

  const [companyPolicyDetail, setCompanyPolicyDetail] = useState(null)

  const [policyTypeOptions, setPolicyTypeOptions] = useState([])
  const [policyType, setPolicyType] = useState(null)
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const [showConfirmAddAttachments, setShowConfirmAddAttachments] = useState({ show: false, item: {} })
  const [showDeleteItem, setShowDeleteItem] = useState({ show: false, type: '', item: {}, index: 0 })
  const [openFilePreview, setOpenFilePreview] = useState(false)

  const formik = useFormik({
    initialValues: {
      company_policy_name: companyPolicyDetail?.name || '',
      company_policy_number:  companyPolicyDetail?.number || '',
      company_policy_description:  companyPolicyDetail?.description || '',
      company_policy_type: companyPolicyDetail?.type_id || '',
      company_policy_is_published: companyPolicyDetail?.is_published || false,
      attachments: companyPolicyDetail?.attachments || []
    },
    validationSchema: Yup.object().shape({
      company_policy_name: Yup.string()
        .required("Please enter company policy name."),
      company_policy_number: Yup.string()
        .required("Please enter company policy number."),
      company_policy_description: Yup.string().required("Please enter company policy description."),
      company_policy_type: Yup.string().required("Please select company policy type."),
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchDetailCompanyPolicy()
    fetchAllPolicyTypes()
  }, [id]);

  const fetchDetailCompanyPolicy = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: `/api/tracker/v1/company-policy/${id}`,
    })
      .then(({ data }) => {
        const { success, value } = data || {}
        if (success) {
          let companyPolicyValue = value
          if (companyPolicyValue?.type_id) {
            setPolicyType({
              label: companyPolicyValue?.policy_type?.name,
              value: companyPolicyValue?.policy_type?.id
            })
          }

          setCompanyPolicyDetail(companyPolicyValue)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllPolicyTypes = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/policy-type",
    })
      .then(({ data }) => {
        setPolicyTypeOptions(singlePolicyTypeOptionsSelect(data?.value))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const handleInputValue = (values) => {
    const formData = new FormData()
    formData.append('name', values.company_policy_name)
    formData.append('number', values.company_policy_number)
    formData.append('description', values.company_policy_description)
    formData.append('type_id', values.company_policy_type)
    formData.append('is_published', values.company_policy_is_published)

    if (values.attachments.length > 0) {
      for (let i = 0; i < values.attachments.length; i++) {
        if (!values?.attachments[i]?.file_url && values?.attachments[i] instanceof File) {
          formData.append('file', values.attachments[i])
        }
      }
    }

    return formData
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/company-policy/update/${id}`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('Company policy has been successfully updated')
          navigate('/company-policy/list')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  const handleSelectPolicyType = (data) => {
    formik.setValues((values) => ({
      ...values,
      company_policy_type: data ? data?.value : ''
    }), false)

    setPolicyType(data ? data : null)
  }

  const handleFileInput = (values) => {
    const files = values

    if (files?.length > 0) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        attachments: [...prevValues['attachments'], ...files]
      }), false)
      
      setShowConfirmAddAttachments({ show: false, item: {} })
    }
  }

  const handleRemoveFile = (index = 0, name) => {
    let prevValue = formik.values['attachments'][index]

    let isDeleted = false
    let url = '/api/tracker/v1/'

    if (prevValue?.file_url) {
      isDeleted = true
      url += `company-policy/delete/attachment/${prevValue.id}`
    }

    if (isDeleted) {
      dispatch({
        type: 'SET_IS_LOADING',
        isLoading: true
      })
      Axios({
        method: "delete",
        url,
      })
        .then(({ data }) => {
          if (data?.success) {
            window.location.reload()
          }
        })
        .catch((err) => {
          console.error(err);
          const errMsg = err?.response?.data?.message
          showError(errMsg)
        })
        .finally(() => {
          dispatch({
            type: 'SET_IS_LOADING',
            isLoading: false
          })
        })
    } else {
      formik.setValues((values) => ({
        ...values,
        [name]: values[name]?.filter((_, i) => i !== index)
      }), false)
    }

    setShowDeleteItem({ show: false, type: '', item: {}, index: 0 })
  }

  const handleCardHeaderButton = () => {
    setShowConfirmAddAttachments({ show: true, item: {} })
  }

  return (
    <>
      {openFilePreview && (
        <AppModalFilePreview
          setOpenFilePreview={setOpenFilePreview}
          file={openFilePreview}
        />
      )}
      {
        showConfirmAddAttachments.show && (
          <AppModalConfirmation 
            confirmationType='add-attachments'
            itemName='attachments'
            itemDetails={showConfirmAddAttachments.item}
            showModal={showConfirmAddAttachments}
            setShowModal={setShowConfirmAddAttachments}
            errors={formik.errors?.attachments}
            handleChange={formik.handleChange}
            handleFileInput={handleFileInput}
            headerTitle={`Add Company Policy Attachments`}
          />
        )
      }
      {
        showDeleteItem.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={`${showDeleteItem?.item?.file_original_name ? showDeleteItem?.item?.file_original_name : showDeleteItem?.item?.file_name ? showDeleteItem?.item?.file_name : showDeleteItem?.item?.name}`}
            itemDetails={showDeleteItem.item}
            itemType={showDeleteItem.type}
            itemIndex={showDeleteItem?.index}
            showModal={showDeleteItem}
            setShowModal={setShowDeleteItem}
            handleConfirmation={handleRemoveFile}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want to edit a company policy?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Edit Company Policy</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            policyTypeOptions={policyTypeOptions}
            policyType={policyType}
            handleSelectPolicyType={handleSelectPolicyType}
          />
        }
        lowerBodyContent={
          <>
            <AppCard
              className='mt-4 mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<h4>Attachments</h4>}
                  headerButton="Upload new"
                  handleCardHeaderButton={handleCardHeaderButton}
                />
              }
              bodyContent={
                <>
                  {
                    formik.values?.attachments?.length > 0 ? (
                      <FormFileThumbnail 
                        name='attachments'
                        colSize='3'
                        files={formik.values?.attachments}
                        className='justify-content-start align-items-center'
                        handleConfirmDelete={setShowDeleteItem}
                        handleViewFile={setOpenFilePreview}
                      />
                    )
                    : (
                      <>Not Found</>
                    )
                  }
                </>
              }
            />
          </>
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/company-policy/list')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default EditCompanyPolicy